import type { UnknownEnum } from './unknown-value.interface';

/**
 * AUE (Auto Update Expiration) time buckets for device state analysis reports.
 *
 * Fixed Time View years are now dynamically calculated by SQL and no longer require
 * annual enum updates. The SQL returns actual year strings (e.g., '2026', '2027', '2031+')
 * based on NOW(), and the frontend handles them as string values for display, color coding,
 * and sorting.
 */
export enum AueTimeFrame {
  // Common buckets
  PastAUE = 'Past AUE',
  Unknown = 'Unknown',

  // Relative Time View buckets (rolling windows from NOW())
  LessThan3Months = '< 3 months',
  LessThan6Months = '< 6 months',
  LessThan12Months = '< 12 months',
  GreaterThan12Months = '> 12 months',
}

/**
 * Helper to check if an AUE timeframe value is a Fixed Time year bucket.
 * Year buckets are 4-digit years or year+ format (e.g., '2026', '2027', '2031+').
 */
export function isFixedTimeYear(value: string): boolean {
  return /^\d{4}\+?$/.test(value);
}

export type AueTimeFrameOrUnknown = AueTimeFrame | UnknownEnum;

/**
 * Type for the fixed-time AUE column. Includes the two named buckets plus year strings
 * dynamically generated by SQL (e.g., '2026', '2027', '2031+'). `string & {}` preserves
 * the named-member display in IDE tooltips while still accepting arbitrary year strings.
 */
export type AueTimeFrameFixed =
  | AueTimeFrame.PastAUE
  | AueTimeFrame.Unknown
  | (string & {});
